import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ShieldAlert } from "lucide-react";
import { useApi } from "@ncp/api-client";
import { useLocale, useTranslation } from "@ncp/i18n";
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Field, InlineAlert, Textarea, useSave } from "@ncp/ui";
import { MIN_OVERRIDE_REASON } from "../ResolveChecklistItemDialog";

/** The finding to override: a stage finding (kind and subject from the checklist) or the warning holding a draft's approval. */
export interface FindingToOverride { kind: string; subjectKey: string; title: string; titleAr?: string | null; detail?: string | null; detailAr?: string | null; overridable?: boolean }

/**
 * An authorised officer's override of a finding of the platform (Conditions 2.19, 2.21, 3.5): the finding as the platform states it, and the
 * officer's reasons, which go into the file's history, the audit trail and the decision memo. Anyone else sees why the stage is held.
 */
export function OverrideFindingDialog({ caseId, finding, mayOverride, open, onOpenChange, onRecorded }: {
  caseId: string; finding: FindingToOverride | null; mayOverride: boolean; open: boolean; onOpenChange: (open: boolean) => void; onRecorded?: () => void;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const api = useApi();
  const qc = useQueryClient();
  const ar = locale === "ar";
  const [reason, setReason] = useState("");
  useEffect(() => { if (open) setReason(""); }, [open, finding?.subjectKey]);

  const record = useSave({
    mutationFn: () => api.post(`/api/cases/${caseId}/review/overrides`, { kind: finding!.kind, subjectKey: finding!.subjectKey, reason: reason.trim() }),
    // A refusal (422 `{ error }`) is reported with the server's own sentence by the save helper.
    saved: t("backoffice:r3Process.finding.recorded"),
    onSuccess: () => {
      for (const k of [["case-review", caseId], ["case-workflow", caseId], ["case-events", caseId], ["case-audit", caseId], ["case-review-history", caseId], ["proposals"], ["case-proposals", caseId]]) void qc.invalidateQueries({ queryKey: k });
      onOpenChange(false);
      onRecorded?.();
    },
  });

  const title = finding ? (ar && finding.titleAr ? finding.titleAr : finding.title) : "";
  const detail = finding ? (ar ? finding.detailAr ?? finding.detail : finding.detail) : null;
  const overridable = finding?.overridable !== false;
  const short = reason.trim().length < MIN_OVERRIDE_REASON;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldAlert className="size-4 text-warning-700" /> {t("backoffice:r3Process.finding.title")}</DialogTitle>
          <DialogDescription dir="auto">{title}</DialogDescription>
        </DialogHeader>
        {detail ? <p className="rounded-lg border border-sand-200 bg-sand-50 px-3 py-2 text-sm text-sand-900" dir="auto">{detail}</p> : null}
        {!overridable ? (
          <InlineAlert tone="info" title={t("backoffice:r3Process.finding.notOverridable")} />
        ) : !mayOverride ? (
          <InlineAlert tone="info" title={t("backoffice:r3Process.finding.authorisedOnly")} />
        ) : (
          <Field label={t("backoffice:r3Process.finding.reason")} required hint={t("backoffice:r3Process.finding.reasonHint")}>
            {(id) => <Textarea id={id} rows={4} value={reason} onChange={(e) => setReason(e.target.value)} draft={finding ? `case:${caseId}:override:${finding.kind}:${finding.subjectKey}:reason` : null} />}
          </Field>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{t("actions.cancel")}</Button>
          {overridable && mayOverride ? <Button variant="gold" disabled={short} loading={record.isPending} onClick={() => record.mutate()}>{t("backoffice:r3Process.finding.record")}</Button> : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
